import { FormControl, FormControlLabel, Row, useTheme } from "@commonlibs";
import { IdentificationCodeField, IdentificationTypeSelect } from "./IdentificationInput.styles";
import { IdentificationInputProps, InnerIdentificationInputProps, IdentificationInputValue } from "./IdentificationInput.types";
import useIdentificationInputValue from "./hooks/useIdentificationInputValue";
import useIdentificationInputOptions from "./hooks/useIdentificationInputOptions";
import useIdentificationInput from "./hooks/useIdentificationInput";

function InnerIdentificationInput({
    value,
    setValue,
    onBlur,
    disabled,
    readonly: readonlyProp,
    invalid,
    showValid,
    hideError,
    setFocused,
    placeholder,
}: InnerIdentificationInputProps): JSX.Element {
    const { spacing } = useTheme();

    const { handleTypeChange, handleCodeChange } = useIdentificationInputValue(value, setValue);
    const identificationTypeOptions = useIdentificationInputOptions();
    const readonlyType = readonlyProp || identificationTypeOptions.length === 1;

    // skipcq: JS-0424
    return (
        <Row gap={spacing[3]} css={{ width: "100%" }}>
            <IdentificationTypeSelect
                value={value[0]}
                options={identificationTypeOptions}
                onChange={handleTypeChange}
                disabled={disabled}
                readonly={readonlyType}
                // skipcq: JS-0424
                dropdownElement={readonlyType ? <></> : undefined}
                error={invalid}
                showValid={showValid}
                hideError={hideError}
                onFocus={() => setFocused(true)}
                onBlur={() => {
                    setFocused(false);
                    onBlur?.(value);
                }}
            />
            <IdentificationCodeField
                type="number"
                value={value[1]}
                onChange={handleCodeChange}
                disabled={disabled}
                readonly={readonlyProp}
                error={invalid}
                showValid={showValid}
                hideError={hideError}
                onFocus={() => setFocused(true)}
                onBlur={() => {
                    setFocused(false);
                    onBlur?.(value);
                }}
                placeholder={placeholder}
            />
        </Row>
    );
}

function IdentificationInput({
    Label = FormControlLabel,
    LabelProps = {},
    defaultValue = ["CC", ""],
    placeholder,
    onChange,
    showValid = false,
    hideError: hideErrorProp = false,
    required = false,
    onBlur,
    ...rest
}: IdentificationInputProps): JSX.Element {
    const { validation, hideError } = useIdentificationInput({ required, defaultValue, onChange });
    return (
        <FormControl<IdentificationInputValue>
            Label={[Label, LabelProps]}
            onChange={onChange}
            defaultValue={defaultValue}
            showValid={showValid}
            hideError={hideError || hideErrorProp}
            validation={validation}
            {...rest}
        >
            {(value, setValue, { disabled, readonly, invalid, setFocused }) => (
                <InnerIdentificationInput
                    value={value}
                    setValue={setValue}
                    disabled={disabled}
                    readonly={readonly}
                    invalid={invalid}
                    setFocused={setFocused}
                    showValid={showValid}
                    hideError={hideError}
                    placeholder={placeholder}
                    required={required}
                    onBlur={onBlur}
                />
            )}
        </FormControl>
    );
}

export default IdentificationInput;
